import axios from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from '../composables/useI18n'

const TOKEN_KEY = 'musex_token'
const USER_ID_KEY = 'musex_user_id'
const USER_ROLE_KEY = 'musex_user_role'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const userId = ref<number | null>(
    localStorage.getItem(USER_ID_KEY) ? Number(localStorage.getItem(USER_ID_KEY)) : null,
  )
  const userRole = ref<number>(Number(localStorage.getItem(USER_ROLE_KEY) || '1'))
  const isInitialized = ref(!token.value)
  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => userRole.value >= 10)

  const login = async (username: string, password: string, captchaToken: string) => {
    const resp = await axios.post('/api/login', { username, password, captchaToken })
    const { code, msg, data } = resp.data
    if (code === 0) {
      token.value = data.token
      userId.value = data.userId
      userRole.value = data.role
      localStorage.setItem(TOKEN_KEY, data.token)
      localStorage.setItem(USER_ID_KEY, String(data.userId))
      localStorage.setItem(USER_ROLE_KEY, String(data.role))
      isInitialized.value = true
      return { success: true }
    }
    return { success: false, message: msg || useI18n().t('common.loginFailed') }
  }

  const logout = () => {
    token.value = null
    userId.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_ID_KEY)
    localStorage.removeItem(USER_ROLE_KEY)
  }

  const autoLogin = async (): Promise<boolean> => {
    if (!token.value) {
      isInitialized.value = true
      return false
    }
    try {
      const resp = await axios.post(
        '/api/login/refresh',
        {},
        { headers: { Authorization: `Bearer ${token.value}` } },
      )
      const { code, data } = resp.data
      if (code === 0) {
        token.value = data.token
        userId.value = data.userId
        userRole.value = data.role
        localStorage.setItem(TOKEN_KEY, data.token)
        localStorage.setItem(USER_ID_KEY, String(data.userId))
        localStorage.setItem(USER_ROLE_KEY, String(data.role))
        isInitialized.value = true
        return true
      }
      // 服务端明确拒绝（token 无效或过期），清除登录态
      logout()
    } catch {
      // 网络错误：保留 token，等待后续 API 请求时再处理鉴权
    }
    isInitialized.value = true
    return false
  }

  const getAuthHeader = () => (token.value ? `Bearer ${token.value}` : null)

  return {
    token,
    userId,
    userRole,
    isAuthenticated,
    isAdmin,
    isInitialized,
    login,
    logout,
    autoLogin,
    getAuthHeader,
  }
})
